import { c as _c } from "react/compiler-runtime";
import { useQuery, useMutation, useQueryClient, useInfiniteQuery, UseInfiniteQueryOptions } from '@tanstack/react-query';
import { entitiesApi } from '../api';
import type { ApiEntity, EntitiesResponse, EntitiesQuery, Entity } from '../api/types';
import { deepMerge } from '../utils/object';

// Query keys
export const queryKeys = {
  entities: ['entities'] as const,
  entity: (id: string) => ['entities', id] as const
} as const;

// Hook to fetch entities with optional filtering and pagination
export function useEntities(query, options) {
  const $ = _c(7);
  let t0;
  let t1;
  if ($[0] !== query) {
    t0 = [...queryKeys.entities, query];
    t1 = async x => {
      const response = await entitiesApi.getEntities({
        ...query,
        nextCursor: x.pageParam as string
      });
      return response;
    };
    $[0] = query;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  const t2 = options as any;
  let t3;
  if ($[3] !== t0 || $[4] !== t1 || $[5] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      staleTime: 10000,
      getNextPageParam: _temp,
      initialPageParam: "",
      ...t2
    };
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return useInfiniteQuery(t3);
}

// Hook to fetch a single entity by ID
function _temp(lastPage) {
  return lastPage.pagination.nextCursor;
}
export function useEntity(id, options) {
  const $ = _c(9);
  let t0;
  let t1;
  if ($[0] !== id || $[1] !== options?.includeInitiatives || $[2] !== options?.initiativeStatus) {
    t0 = [...queryKeys.entity(id), options?.includeInitiatives, options?.initiativeStatus];
    t1 = async () => {
      const apiEntity = await entitiesApi.getEntity(id, {
        includeInitiatives: options?.includeInitiatives,
        initiativeStatus: options?.initiativeStatus
      });
      return apiEntity;
    };
    $[0] = id;
    $[1] = options?.includeInitiatives;
    $[2] = options?.initiativeStatus;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  const t2 = !!id;
  let t3;
  if ($[5] !== t0 || $[6] !== t1 || $[7] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      staleTime: 10000
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return useQuery(t3);
}

// Hook to fetch entity initiatives with visibility filtering
export function useEntityInitiatives(id, options) {
  const $ = _c(9);
  let t0;
  let t1;
  if ($[0] !== id || $[1] !== options?.limit || $[2] !== options?.status) {
    t0 = [...queryKeys.entity(id), "initiatives", options?.status, options?.limit];
    t1 = async () => entitiesApi.getEntityInitiatives(id, {
      status: options?.status,
      limit: options?.limit
    });
    $[0] = id;
    $[1] = options?.limit;
    $[2] = options?.status;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  const t2 = options?.enabled !== false && !!id;
  let t3;
  if ($[5] !== t0 || $[6] !== t1 || $[7] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      staleTime: 10000
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return useQuery(t3);
}

// Hook to create an entity with optimistic updates
export function useCreateEntity() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp2,
      throwOnError: false,
      onMutate: async data_0 => {
        await queryClient.cancelQueries({
          queryKey: queryKeys.entities
        });
        const previousQueries = queryClient.getQueriesData({
          queryKey: queryKeys.entities
        });
        const optimisticEntity = {
          id: `temp-${Date.now()}`,
          type: data_0.type || "organisation",
          name_1: data_0.name_1 || "",
          name_2: data_0.name_2 || null,
          description: data_0.description || null,
          image: data_0.image || null,
          website: data_0.website || null,
          primary_email: data_0.primary_email || null,
          primary_phone: data_0.primary_phone || null,
          status: data_0.status || "active",
          data: data_0.data || null,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          deleted: false,
          ...data_0
        } as ApiEntity;
        queryClient.setQueriesData({
          queryKey: queryKeys.entities
        }, old => {
          if (!old?.pages) {
            return old;
          }
          return {
            ...old,
            pages: old.pages.map((page, index) => {
              if (index === 0) {
                return {
                  ...page,
                  entities: [optimisticEntity, ...page.entities]
                };
              }
              return page;
            })
          };
        });
        return {
          previousQueries,
          optimisticEntity
        };
      },
      onSuccess: (newEntity_0, _variables, context) => {
        queryClient.setQueriesData({
          queryKey: queryKeys.entities
        }, old_0 => {
          if (!old_0?.pages) {
            return old_0;
          }
          return {
            ...old_0,
            pages: old_0.pages.map((page_0, index_0) => {
              if (index_0 === 0) {
                return {
                  ...page_0,
                  entities: page_0.entities.map(entity => entity.id === context?.optimisticEntity.id ? newEntity_0 : entity)
                };
              }
              return page_0;
            })
          };
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.entities
        });
      },
      onError: (_err, _variables_0, context_0) => {
        if (context_0?.previousQueries) {
          context_0.previousQueries.forEach(t1 => {
            const [queryKey, data_1] = t1;
            queryClient.setQueryData(queryKey, data_1);
          });
        }
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}

// Hook to update an entity with optimistic updates
async function _temp2(data) {
  const newEntity = await entitiesApi.createEntity(data);
  return newEntity;
}
export function useUpdateEntity() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp3,
      throwOnError: false,
      onMutate: async t1 => {
        const {
          id: id_0,
          data: data_0
        } = t1;
        await queryClient.cancelQueries({
          queryKey: queryKeys.entities
        });
        await queryClient.cancelQueries({
          queryKey: ["entities", id_0]
        });
        await queryClient.cancelQueries({
          queryKey: ["admin-entities"]
        });
        const previousEntityQueries = queryClient.getQueriesData({
          queryKey: ["entities", id_0]
        });
        const previousEntitiesQueries = queryClient.getQueriesData({
          queryKey: queryKeys.entities
        });
        const previousAdminQueries = queryClient.getQueriesData({
          queryKey: ["admin-entities"]
        });
        const mergeEntityData = oldEntity => {
          if (!data_0.data || !oldEntity.data) {
            return {
              ...oldEntity,
              ...data_0
            };
          }
          const mergedData = deepMerge(oldEntity.data, data_0.data);
          return {
            ...oldEntity,
            ...data_0,
            data: mergedData
          };
        };
        queryClient.setQueriesData({
          queryKey: ["entities", id_0]
        }, old => {
          if (!old) {
            return old;
          }
          return mergeEntityData(old);
        });
        queryClient.setQueriesData({
          queryKey: queryKeys.entities
        }, old_0 => {
          if (!old_0?.pages) {
            return old_0;
          }
          return {
            ...old_0,
            pages: old_0.pages.map(page => ({
              ...page,
              entities: page.entities.map(entity => {
                if (String(entity.id) !== id_0) {
                  return entity;
                }
                return mergeEntityData(entity);
              })
            }))
          };
        });
        queryClient.setQueriesData({
          queryKey: ["admin-entities"]
        }, old_1 => {
          if (!old_1?.pages) {
            return old_1;
          }
          return {
            ...old_1,
            pages: old_1.pages.map(page_0 => ({
              ...page_0,
              entities: page_0.entities.map(entity_0 => {
                if (String(entity_0.id) !== id_0) {
                  return entity_0;
                }
                return mergeEntityData(entity_0);
              })
            }))
          };
        });
        return {
          previousEntityQueries,
          previousEntitiesQueries,
          previousAdminQueries
        };
      },
      onSuccess: (updatedEntity_0, t2) => {
        const {
          id: id_1
        } = t2;
        queryClient.setQueriesData({
          queryKey: ["entities", id_1]
        }, updatedEntity_0);
        queryClient.setQueriesData({
          queryKey: queryKeys.entities
        }, old_2 => {
          if (!old_2?.pages) {
            return old_2;
          }
          return {
            ...old_2,
            pages: old_2.pages.map(page_1 => ({
              ...page_1,
              entities: page_1.entities.map(entity_1 => String(entity_1.id) === id_1 ? updatedEntity_0 : entity_1)
            }))
          };
        });
        queryClient.setQueriesData({
          queryKey: ["admin-entities"]
        }, old_3 => {
          if (!old_3?.pages) {
            return old_3;
          }
          return {
            ...old_3,
            pages: old_3.pages.map(page_2 => ({
              ...page_2,
              entities: page_2.entities.map(entity_2 => String(entity_2.id) === id_1 ? updatedEntity_0 : entity_2)
            }))
          };
        });
      },
      onError: (_err, _variables, context) => {
        if (context?.previousEntityQueries) {
          context.previousEntityQueries.forEach(t3 => {
            const [queryKey, data_1] = t3;
            queryClient.setQueryData(queryKey, data_1);
          });
        }
        if (context?.previousEntitiesQueries) {
          context.previousEntitiesQueries.forEach(t4 => {
            const [queryKey_0, data_2] = t4;
            queryClient.setQueryData(queryKey_0, data_2);
          });
        }
        if (context?.previousAdminQueries) {
          context.previousAdminQueries.forEach(t5 => {
            const [queryKey_1, data_3] = t5;
            queryClient.setQueryData(queryKey_1, data_3);
          });
        }
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}
async function _temp3(t0) {
  const {
    id,
    data
  } = t0;
  const updatedEntity = await entitiesApi.updateEntity(id, data);
  return updatedEntity;
}